import { useState, type FormEvent } from 'react';
import { ApiError, type CreatedTaxSubject, type AmoyeApi } from '../lib/api';
import type { PersonaId } from '../types/persona';
import { Button } from './Button';

type TaxType = CreatedTaxSubject['tax_types'][number];

export function FirstUseSetup({ api, product, onComplete }: { api: AmoyeApi; product: PersonaId;
  onComplete: (subject: CreatedTaxSubject) => void }) {
  return <TaxSubjectSetup api={api} product={product} onComplete={onComplete} />;
}

export function TaxSubjectSetupDialog({ api, product, onCancel, onComplete }: { api: AmoyeApi; product: PersonaId;
  onCancel: () => void; onComplete: (subject: CreatedTaxSubject) => void }) {
  const copy = setupCopy(product);
  return <div aria-labelledby="tax-subject-setup-title" aria-modal="true" className="am-subject-setup-dialog" role="dialog">
    <section className="am-first-use__card">
      <header className="am-subject-setup-dialog__header"><div><p className="eyebrow">{copy.eyebrow}</p>
        <h1 id="tax-subject-setup-title">{copy.title}</h1></div>
        <Button aria-label="Close client setup" onClick={onCancel} size="small" tone="quiet">Close</Button></header>
      <TaxSubjectSetup api={api} product={product} onComplete={onComplete} showHeading={false} />
    </section>
  </div>;
}

export function PersonalTaxYearDialog({ api, existingYears, onCancel, onComplete }: { api: AmoyeApi;
  existingYears: readonly number[]; onCancel: () => void; onComplete: (taxYear: number) => void }) {
  const nextYear = Math.max(new Date().getUTCFullYear(), ...(existingYears.length ? existingYears.map((year) => year + 1) : [0]));
  const [taxYear, setTaxYear] = useState(nextYear);
  const [pending, setPending] = useState(false); const [error, setError] = useState('');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setPending(true); setError('');
    try { const created = await api.createPersonalTaxYear(taxYear); onComplete(created.tax_year); }
    catch (reason) { setError(reason instanceof ApiError && reason.status === 409
      ? `Personal Tax ${taxYear} already exists.` : 'The tax year could not be started. Check the details and try again.'); }
    finally { setPending(false); }
  };
  return <div aria-labelledby="personal-tax-year-title" aria-modal="true" className="am-subject-setup-dialog" role="dialog">
    <section className="am-first-use__card"><header className="am-subject-setup-dialog__header"><div>
      <p className="eyebrow">TAX YEAR</p><h1 id="personal-tax-year-title">Start another tax year</h1></div>
      <Button aria-label="Close tax year setup" onClick={onCancel} size="small" tone="quiet">Close</Button></header>
      <p className="am-subject-setup__description">Create a clean annual tax case for the same verified individual. Existing years and evidence are not copied.</p>
      <form onSubmit={submit}><label className="field-label" htmlFor="personal-tax-year">Tax year</label>
        <input id="personal-tax-year" max={2100} min={2000} onChange={(event) => setTaxYear(Number(event.target.value))}
          required type="number" value={taxYear} />
        {error && <p className="login-card__error" role="alert">{error}</p>}
        <Button disabled={pending || existingYears.includes(taxYear)} type="submit">{pending ? 'Starting…' : 'Start tax year'}</Button>
      </form>
    </section>
  </div>;
}

function TaxSubjectSetup({ api, product, onComplete, showHeading = true }: { api: AmoyeApi; product: PersonaId;
  onComplete: (subject: CreatedTaxSubject) => void; showHeading?: boolean }) {
  const fixedKind = product === 'individual' ? 'individual' : product === 'business' ? 'organization' : undefined;
  const [kind, setKind] = useState<'organization' | 'individual'>(fixedKind ?? 'organization');
  const [name, setName] = useState('');
  const [tin, setTin] = useState('');
  const [taxTypes, setTaxTypes] = useState<TaxType[]>(product === 'individual' ? ['PIT'] : ['VAT', 'CIT']);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const copy = setupCopy(product);

  function changeKind(next: 'organization' | 'individual') {
    setKind(next);
    setTaxTypes(next === 'individual' ? ['PIT'] : ['VAT', 'CIT']);
  }

  function toggleTaxType(value: TaxType) {
    setTaxTypes((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (taxTypes.length === 0) { setError('Select at least one tax obligation to continue.'); return; }
    setPending(true); setError('');
    try {
      const subject = await api.createTaxSubjectSetup({ display_name: name.trim(),
        ...(tin.trim() ? { tin: tin.trim() } : {}), subject_kind: kind, tax_types: taxTypes });
      onComplete(subject);
    } catch (reason) {
      setError(reason instanceof ApiError && reason.status === 409
        ? reason.message
        : 'Setup could not be completed. Check the details and try again.');
    } finally { setPending(false); }
  }

  const choices: Array<{ id: TaxType; label: string; hint: string }> = kind === 'individual'
    ? [{ id: 'PIT', label: 'Personal income tax', hint: 'Annual personal tax return.' }]
    : [
      { id: 'VAT', label: 'Value added tax', hint: 'For VAT-registered taxable supplies.' },
      { id: 'CIT', label: 'Company income tax', hint: 'Annual company tax position.' },
      { id: 'WHT', label: 'Withholding tax', hint: 'When the business deducts tax from covered payments.' },
      { id: 'PAYE', label: 'PAYE', hint: 'When the business has employees and deducts payroll tax.' },
    ];

  const content = <>
      {showHeading && <><p className="eyebrow">{copy.eyebrow}</p>
        <h1 id="first-use-title">{copy.title}</h1></>}
      <p className="am-subject-setup__description">{copy.description}</p>
      <form onSubmit={submit} aria-busy={pending}>
        {!fixedKind && <label className="field-label" htmlFor="subject-kind">Client type
          <select id="subject-kind" value={kind} onChange={(event) => changeKind(event.target.value as typeof kind)}>
            <option value="organization">Business or organization</option>
            <option value="individual">Individual</option>
          </select>
        </label>}
        <label className="field-label" htmlFor="subject-name">{copy.nameLabel}</label>
        <input autoFocus id="subject-name" required minLength={2} maxLength={200} value={name}
          onChange={(event) => setName(event.target.value)} placeholder={copy.nameHint} />
        <label className="field-label" htmlFor="subject-tin">Tax identification number <span>(optional)</span></label>
        <input id="subject-tin" maxLength={64} value={tin} onChange={(event) => setTin(event.target.value)}
          placeholder="Enter the registered TIN if available" />
        <fieldset className="am-first-use__taxes">
          <legend>Taxes to manage</legend>
          <p>Select what applies now. This determines which return areas are created.</p>
          {choices.map((choice) => <label key={choice.id}>
            <input checked={taxTypes.includes(choice.id)} type="checkbox" onChange={() => toggleTaxType(choice.id)} />
            <span><strong>{choice.label}</strong><small>{choice.hint}</small></span>
          </label>)}
        </fieldset>
        <p className="am-first-use__note">You can add records after setup. An entered Tax ID is recorded as pending verification; Amoye does not treat it as registry-verified.</p>
        {error && <p className="login-card__error" role="alert">{error}</p>}
        <Button disabled={pending} type="submit">{pending ? 'Creating…' : copy.action}</Button>
      </form>
    </>;
  return showHeading ? <main className="am-first-use" aria-labelledby="first-use-title">
    <section className="am-first-use__card">{content}</section>
  </main> : content;
}

function setupCopy(product: PersonaId) {
  if (product === 'practitioner') return { eyebrow: 'CLIENT SETUP', title: 'Add a client',
    description: 'Create the client record, choose the taxes you are engaged to manage, and assign the work to yourself.',
    nameLabel: 'Client legal name', nameHint: 'For example, Northstar Foods Limited', action: 'Create client' };
  if (product === 'business') return { eyebrow: 'FIRST SETUP', title: 'Set up your business tax profile',
    description: 'Tell us which business is filing and which taxes currently apply.',
    nameLabel: 'Registered business name', nameHint: 'For example, Sunrise Creative Limited', action: 'Create business tax profile' };
  return { eyebrow: 'FIRST SETUP', title: 'Set up your personal tax profile',
    description: 'Confirm whose personal income tax records this account will manage.',
    nameLabel: 'Full legal name', nameHint: 'For example, Chidinma Eze', action: 'Create personal tax profile' };
}
